import File from '@/components/presentational/img/icons/File';
import Link from '@/components/presentational/img/icons/Link';
import Sound from '@/components/presentational/img/icons/Sound';
import Video from '@/components/presentational/img/icons/Video';
import { Media } from '@/types/core/Media';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Icon,
} from '@chakra-ui/react';
import ButtonWithConfirmTooltip from '@components/app/ButtonWithConfirmTooltip';
import { FileInputForm } from '@components/app/form/FileInputForm';
import TextArea from '@components/app/form/TextArea';
import TextInput from '@components/app/form/TextInput';
import FormElement from '@components/presentational/Form/FormElement';
import MediaBlocTabHeader from '@components/presentational/MediaBlocs/MediaBlocTabHeader';
import { Field } from 'formik';
import { useRef } from 'react';
import { PiTrashThin } from 'react-icons/pi';

function FileMedia({ name, isDisabled, withAlt }) {
  return (
    <>
      <Alert variant="top-accent" borderBottomRightRadius="0.9rem">
        <AlertIcon />
        <AlertDescription fontSize="0.75rem" lineHeight="1rem">
          Formats : pdf doc docx odt xls xlsx ppt pptx rar zip 7zip prezi bi cff
          flp notebook xbk nbk gwb yar flipchart png jpg jpeg gif mpeg mpg mp4
          mov avi wmv xvid ogg ogm mkv mp3
        </AlertDescription>
      </Alert>
      <FormElement label="Document attaché">
        <FileInputForm
          name={`${name}.file`}
          required
          allowedExtensions={[
            // types for documents
            'pdf',
            'doc',
            'docx',
            'odt',
            'xls',
            'xlsx',
            'ppt',
            'pptx',
            'rar',
            'zip',
            '7zip',
            'prezi',
            // types for digital whiteboard
            'bi',
            'cff',
            'flp',
            'notebook',
            'xbk',
            'nbk',
            'gwb',
            'yar',
            'flipchart',
            // types for images
            'png',
            'jpg',
            'jpeg',
            'gif',
            // types for videos
            'mpeg',
            'mpg',
            'mp4',
            'mov',
            'avi',
            'wmv',
            'xvid',
            'ogg',
            'ogm',
            'mkv',
            // types for sounds
            'mp3',
            'mpga',
          ]}
          allowedMaxSize={100}
          isDisabled={isDisabled}
        />
        <TextInput
          name={`${name}.title`}
          label={'Titre'}
          required
          size={'sm'}
        />
        {withAlt && (
          <TextInput
            name={`${name}.alt`}
            label={'Texte alternatif'}
            size={'sm'}
          />
        )}
        <TextArea name={`${name}.description`} label={'Description'} />
      </FormElement>
    </>
  );
}

function LinkMedia({ name }) {
  return (
    <>
      <TextInput
        size={'sm'}
        type={'url'}
        required
        label="URL"
        name={`${name}.url`}
      />
      <TextInput size={'sm'} required label="Titre" name={`${name}.title`} />
      <TextArea label="Description" name={`${name}.description`} />
    </>
  );
}

function VideoMedia({ name }) {
  return (
    <>
      <TextInput
        size={'sm'}
        type={'url'}
        required
        label="URL"
        name={`${name}.url`}
      />
      <TextInput size={'sm'} required label="Titre" name={`${name}.title`} />
      <TextArea required label="Description" name={`${name}.description`} />
    </>
  );
}
function SoundMedia({ name }) {
  return (
    <>
      <TextInput
        size={'sm'}
        type={'url'}
        required
        label="URL"
        name={`${name}.url`}
      />
      <TextInput size={'sm'} required label="Titre" name={`${name}.title`} />
      <TextArea required label="Description" name={`${name}.description`} />
    </>
  );
}

export default function MediaForm({
  name,
  media,
  onMediaRemove,
  isDisabled = false,
  withAlt = false,
  showOnlyMediaTab,
}: {
  name: string;
  media: Media;
  onMediaRemove?: () => void;
  isDisabled?: boolean;
  withAlt?: boolean;
  showOnlyMediaTab?: 'fileMedia' | 'soundMedia' | 'linkMedia' | 'videoMedia';
}) {
  const ref = useRef(null);

  return (
    <Flex
      direction="column"
      gap={['0.5rem', '', '1rem']}
      border="1px solid"
      borderColor="tournesol.100"
      padding="1rem"
      position="relative"
      ref={ref}
    >
      <Field name={`${name}.__TYPE__`}>
        {({ field, form }) => {
          if (media.id || showOnlyMediaTab) {
            const mediaType = showOnlyMediaTab
              ? showOnlyMediaTab
              : media.__TYPE__;
            let Component = null;
            let label = '';
            switch (mediaType) {
              case 'fileMedia':
                Component = File;
                label = 'Fichier';
                break;
              case 'soundMedia':
                Component = Sound;
                label = 'Son';
                break;
              case 'linkMedia':
                Component = Link;
                label = 'URL';
                break;
              case 'videoMedia':
                Component = Video;
                label = 'Vidéo';
                break;
            }

            return (
              <Flex>
                <Component iconWidth="1.125em" iconMarginRight=".5em" />
                {label}
              </Flex>
            );
          }
          return (
            <>
              <MediaBlocTabHeader
                selectedTab={media.__TYPE__}
                setSelectedTab={tab => {
                  field.onChange(`${name}.__TYPE__`)(tab);
                }}
              />
            </>
          );
        }}
      </Field>
      <Flex direction="column" gap={['0.5rem', '', '1rem']}>
        {media.__TYPE__ === 'fileMedia' && (
          <FileMedia name={name} isDisabled={isDisabled} withAlt={withAlt} />
        )}
        {media.__TYPE__ === 'videoMedia' && <VideoMedia name={name} />}
        {media.__TYPE__ === 'soundMedia' && <SoundMedia name={name} />}
        {media.__TYPE__ === 'linkMedia' && <LinkMedia name={name} />}
      </Flex>
      {onMediaRemove && (
        <Box position="absolute" top="-1rem" right="-1rem">
          <ButtonWithConfirmTooltip
            size="sm"
            variant="danger"
            width="1rem"
            aspectRatio="1/1"
            sx={{ padding: '0' }}
            onClickConfirm={async () => {
              onMediaRemove();
            }}
            containerRef={ref}
          >
            <Icon as={PiTrashThin} boxSize="1rem" />
          </ButtonWithConfirmTooltip>
        </Box>
      )}
    </Flex>
  );
}
